import React from 'react';
import { motion } from 'framer-motion';
import { Film, Sparkles, FolderOpen } from 'lucide-react';
import { SceneVideo } from './LandingDemo';

// Each feature card plays a pre-rendered scene video on loop (compiled from
// the live React reel by apps/web/scripts/render-reel-scenes.mjs).
// Page-wide scene allocation (one scene per landing spot):
//   hero-reel -> LandingHero (above-fold walkthrough)
//   describe  -> LandingDemo step 1 (Write)
//   objects   -> LandingDemo step 2 (Generate)
//   wire      -> LandingDemo step 3 (Animate) + Storyboard Studio card here
//                (intentional reuse - wire is the canonical storyboard scene)
//   export    -> LandingDemo step 4 (Ship)
//   build     -> AI Generation Lab card (generation tiles filling)
//   media     -> Media Library card (searchable asset grid)
const FEATURES = [
    {
        icon: Film,
        color: 'from-[var(--primary-color)] to-[#0D52BC]',
        glow: 'rgba(24,181,240,0.2)',
        title: 'Storyboard Studio',
        description: 'Drag scenes around like sticky notes on a conspiracy board. Node-based editor with a timeline that actually makes sense. Your story, your chaos.',
        tags: ['Storyboard', 'Timeline', 'Node Editor'],
        slug: 'wire',
        mediaAlt: 'Helix storyboard canvas with connected scenes',
    },
    {
        icon: Sparkles,
        color: 'from-purple-500 to-pink-500',
        glow: 'rgba(168,85,247,0.2)',
        title: 'AI Generation Lab',
        description: 'Tell Helix what you see in your head and watch it materialize. Images, video, audio. Save your characters as Objects so they show up consistently, like actual actors.',
        tags: ['Image Gen', 'Video Gen', 'Objects Library'],
        slug: 'build',
        mediaAlt: 'Generating an image with character object references',
    },
    {
        icon: FolderOpen,
        color: 'from-emerald-500 to-teal-500',
        glow: 'rgba(16,185,129,0.2)',
        title: 'Media Library',
        description: 'Every image, clip, and sound byte you create lives here. Search it, filter it, drag it straight onto your storyboard. No more "where did I save that?"',
        tags: ['Asset Library', 'Upload', 'Drag & Drop'],
        slug: 'media',
        mediaAlt: 'Personal Library grid with assets and filters',
    },
];

// Reuses SceneVideo (with its IntersectionObserver play-on-visible fix for
// iOS Safari) wrapped in the card's aspect-ratio container.
const FeatureMedia = ({ slug, alt }) => (
    <div className="w-full aspect-video rounded-xl overflow-hidden mb-6 ring-1 ring-white/8">
        <SceneVideo slug={slug} className="h-full object-cover" ariaLabel={alt} />
    </div>
);

const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
    }),
};

const LandingFeatures = () => {
    return (
        <section id="features" className="relative py-32 px-6">
            <div className="max-w-7xl mx-auto">
                {/* Section header */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-20"
                >
                    <span className="inline-block text-[var(--primary-color)] text-xs font-bold uppercase tracking-widest mb-4">What You Get</span>
                    <h2 className="text-4xl md:text-5xl font-heading font-black tracking-tight text-white">
                        Everything. In one place. Finally.
                    </h2>
                    <p className="mt-4 text-lg text-[var(--text-secondary)] max-w-2xl mx-auto">
                        Stop juggling twelve tabs and three subscriptions. Helix is the whole studio.
                    </p>
                </motion.div>

                {/* Feature cards */}
                <div className="grid md:grid-cols-3 gap-6">
                    {FEATURES.map(({ icon: Icon, color, glow, title, description, tags, slug, mediaAlt }, i) => (
                        <motion.div
                            key={title}
                            custom={i}
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: '-60px' }}
                            className="glass-panel rounded-2xl p-6 border border-white/5 hover:border-white/10 transition-all group relative overflow-hidden"
                        >
                            {/* Background glow */}
                            <div
                                className="absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                                style={{ background: glow }}
                            />

                            {/* Feature animation — pre-rendered scene video on loop */}
                            <FeatureMedia slug={slug} alt={mediaAlt} />

                            {/* Icon */}
                            <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-4 shadow-lg`}>
                                <Icon size={20} className="text-white" />
                            </div>

                            <h3 className="text-xl font-heading font-bold text-white mb-3">{title}</h3>
                            <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-6">{description}</p>

                            {/* Tags */}
                            <div className="flex flex-wrap gap-2">
                                {tags.map(tag => (
                                    <span key={tag} className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)] bg-white/5 px-2.5 py-1 rounded-lg border border-white/5">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default LandingFeatures;
